
import React, { useState } from 'react';
import { SafetyMatrix } from '../types';
import { ShieldAlert, Volume2, MessageSquare, Sword, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SafetyBadgeProps {
  matrix: SafetyMatrix;
}

const SafetyBadge: React.FC<SafetyBadgeProps> = ({ matrix }) => {
  const [activeInfo, setActiveInfo] = useState<number | null>(null);

  const items = [
    { 
      label: 'Deep Topics', 
      val: matrix.thematicWeight, 
      icon: ShieldAlert, 
      color: 'text-amber-400',
      desc: 'How heavy or sad the story gets. High scores mean it deals with complex emotions or loss.'
    },
    { 
      label: 'Sights & Sounds', 
      val: matrix.sensoryLoad, 
      icon: Volume2, 
      color: 'text-blue-400',
      desc: 'Intensity for the senses. High scores mean loud noises, bright flashing, or fast-paced editing.'
    },
    { 
      label: 'Language & Jokes', 
      val: matrix.dialogueCheck, 
      icon: MessageSquare, 
      color: 'text-green-400',
      desc: 'Checks for adult humor, double meanings, or bad language that might need explaining.'
    },
    { 
      label: 'Action & Scares', 
      val: matrix.visualViolence, 
      icon: Sword, 
      color: 'text-red-400',
      desc: 'Checks for scary creatures, physical fighting, or intense chase scenes.'
    },
  ];

  return (
    <div className="relative">
      <div className="bg-black/60 rounded-lg p-2 border border-[#d4af37]/30 backdrop-blur-sm grid grid-cols-2 gap-2 text-[10px] uppercase font-bold relative">
        {items.map((item, idx) => (
          <button 
            key={idx} 
            onClick={(e) => {
              e.stopPropagation();
              setActiveInfo(activeInfo === idx ? null : idx);
            }}
            className="flex items-center gap-1 text-left hover:bg-white/5 p-1 rounded transition-colors group/item"
          >
            <item.icon size={12} className={`${item.color} group-hover/item:scale-110 transition-transform`} />
            <div className="flex-1">
              <div className="flex justify-between mb-[2px] items-center">
                <span>{item.label}</span>
                <span className="opacity-60">{item.val}/10</span>
              </div>
              <div className="w-full bg-white/10 h-1 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${item.color.replace('text', 'bg')}`} 
                  style={{ width: `${item.val * 10}%` }}
                />
              </div>
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {activeInfo !== null && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-full left-0 right-0 mb-2 z-50 bg-[#3a0303] border border-[#d4af37] p-3 rounded shadow-2xl"
          >
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center gap-2">
                {React.createElement(items[activeInfo].icon, { size: 14, className: items[activeInfo].color })}
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#d4af37]">
                  {items[activeInfo].label}
                </span>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setActiveInfo(null); }}
                className="text-white/40 hover:text-white"
              >
                <X size={12} />
              </button>
            </div>
            <p className="text-[10px] leading-relaxed text-[#fef3c7]/90 font-serif-elegant italic">
              {items[activeInfo].desc}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SafetyBadge;
